import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { getEmailLog } from "../services/signupService";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import JBWDateLabel from "./JBWDateLabel";

// Keyed on tblEmailLog.emailType, JB360's own names for each email. Unknown values fall back
// to the raw name in the column below.
const EMAIL_TYPE_LABELS = {
  WELCOME_INVITE: "Welcome Invite",
  PASSWORD_RESET: "Password Reset",
  EMAIL_VERIFY: "Email Verification",
  DEALER_DIGEST: "Dealer Pending Items Digest",
  CONTACT_US: "Contact Us",
  CONTACT_US_COPY: "Contact Us (copy)",
};

// tblEmailLog.sendStatus is what the sender knew at send time, never delivery. Anything not
// listed here is SES's own failure status (or FAILED:<error>), shown as stored.
const SEND_STATUS_LABELS = {
  REQUESTED: "Requested",
  ACCEPTED: "Accepted by SES",
};

const ALL_TYPES = "ALL";

// GET /emaillog returns at most this many rows, newest first, within the chosen type.
const MAX_ROWS = 500;

const typeLabel = (type) => EMAIL_TYPE_LABELS[type] || type;

const columns = [
  {
    // Requested, not sent: the row is written when the send is asked for, and nothing
    // confirms delivery.
    field: "requestedOnUtc",
    headerName: "Requested",
    width: 190,
    renderCell: (param) =>
      param.value ? (
        <JBWDateLabel
          inputDate={param.value}
          dateFormat="MMM DD, YYYY h:mm A"
          local
        />
      ) : (
        ""
      ),
  },
  {
    field: "emailType",
    headerName: "Type",
    width: 210,
    valueGetter: (param) => typeLabel(param.value),
  },
  {
    field: "sendStatus",
    headerName: "Status",
    width: 150,
    valueGetter: (param) => SEND_STATUS_LABELS[param.value] || param.value,
  },
  {
    field: "subject",
    headerName: "Subject",
    flex: 1,
    minWidth: 220,
  },
];

const EmailHistoryDialog = ({ open, onClose, user }) => {
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);

  const email = user?.registeredEmail;
  const capped = emails.length >= MAX_ROWS;

  // A new user, or the dialog reopening, starts from all types.
  useEffect(() => {
    setTypeFilter(ALL_TYPES);
  }, [open, email]);

  // The type filter is applied by the server, so the row cap falls within the chosen type: a
  // browser-side filter over the newest rows would hide an older email of another type and
  // report that it was never sent.
  useEffect(() => {
    setEmails([]);
    setLoadErr("");
    if (!open || !email) {
      setIsLoading(false);
      return;
    }
    let ignore = false;
    setIsLoading(true);
    getEmailLog(email, typeFilter === ALL_TYPES ? undefined : typeFilter)
      .then((res) => {
        if (ignore) return;
        setEmails(Array.isArray(res.data) ? res.data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        if (ignore) return;
        console.error("email log loading error", err);
        setLoadErr(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load the email history."
        );
        setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [open, email, typeFilter]);

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="md">
      <DialogTitle>Email History &mdash; {email}</DialogTitle>
      <DialogContent>
        {loadErr ? (
          <Typography variant="body2" sx={{ color: "#f44336" }}>
            {loadErr}
          </Typography>
        ) : (
          <>
            <TextField
              id="email-history-type-filter"
              select
              size="small"
              label="Email type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 260, mb: 2, mt: 1 }}
            >
              <MenuItem value={ALL_TYPES}>All types</MenuItem>
              {Object.keys(EMAIL_TYPE_LABELS).map((type) => (
                <MenuItem key={type} value={type}>
                  {typeLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            {capped && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Showing the newest {MAX_ROWS} emails
                {typeFilter === ALL_TYPES ? "" : " of this type"}.
              </Typography>
            )}
            <DataGrid
              rows={emails}
              getRowId={(row) => row.id}
              columns={columns}
              loading={isLoading}
              autoHeight
              disableColumnFilter
              disableColumnMenu
              slots={{ noRowsOverlay: CustomNoRowsOverlay }}
              slotProps={{
                noRowsOverlay: {
                  message:
                    typeFilter === ALL_TYPES
                      ? "No emails logged for this user"
                      : "No emails of this type for this user",
                },
              }}
              pageSizeOptions={[10]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailHistoryDialog;
